import React, { lazy, Suspense } from 'react';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  type DataFunctionArgs,
  type HeadersFunction,
  json,
  type LinksFunction,
  type V2_MetaFunction } from
'@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { getTheme } from './routes/resources+/theme/theme-session.server.ts';
import fontStylestylesheetUrl from './styles/font.css';
import tailwindStylesheetUrl from './styles/tailwind.css';
import { authenticator, getUserId } from './utils/auth.server.ts';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { combineHeaders, getDomainUrl } from './utils/misc.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { useOptionalUser } from './utils/user.ts';
import { href as iconsHref } from './components/ui/icon.tsx';
import { Confetti } from './components/confetti.tsx';
import { getFlashSession } from './utils/flash-session.server.ts';
import { useToast } from './utils/useToast.tsx';
import { Toaster } from './components/ui/toaster.tsx';
import { ExposeAppConfig } from '~/components/ExposeAppConfig/index.ts';
import { GoogleAnalytics } from '~/components/GoogleAnalytics/index.tsx';
import {
  type ConsentData,
  CookieConsentBanner,
  CookieConsentProvider } from
'~/components/CookieConsent/index.tsx';
import { getSession } from '~/utils/session.server.ts';
import { defaultNS } from '~/i18n/i18n.config.ts';
import i18next from '~/i18n/i18next.server.ts';
import { useChangeLanguage } from '~/i18n/useChangeLanguage.ts';
import { useDirection } from '~/i18n/use-i18n.hook.ts';
import { Header } from '~/components/Header.tsx';
import Footer from '~/components/Footer.tsx';
import { getCartId } from '~/utils/cart.server.ts';
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx';
import rdtStylesheetUrl from 'remix-development-tools/stylesheet.css';

const RemixDevTools =
process.env.NODE_ENV === 'development' ?
lazy(() => import('remix-development-tools')) :
undefined;

// TODO: Implement the logic to handle guest users in the event that the user is not logged in

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStylestylesheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStylesheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  rdtStylesheetUrl && process.env.NODE_ENV === 'development' ?
  { rel: 'preload', href: rdtStylesheetUrl, as: 'style' } :
  null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: fontStylestylesheetUrl },
  { rel: 'stylesheet', href: tailwindStylesheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null,
  rdtStylesheetUrl && process.env.NODE_ENV === 'development' ?
  { rel: 'stylesheet', href: rdtStylesheetUrl } :
  null].
  filter(Boolean);
};

export const meta: V2_MetaFunction = () => {
  return [
  { title: 'PetCanvas' },
  {
    name: 'description',
    content: 'Beautiful high-quality custom pet portraits in any style.'
  }];

};

export const handle = {
  i18n: defaultNS
};

export async function loader({ request }: DataFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const { cartId, headers: cartHeaders } = await time(
    () => getCartId(request),
    {
      timings,
      type: 'getCartId',
      desc: 'getCartId in root'
    }
  );

  // console.log('root loader userId', userId)
  // console.log('root loader cartId', cartId)

  const user = userId ?
  await time(
    () =>
    prisma.user.findUnique({
      where: { id: userId },
      select: { id: true, name: true, username: true, imageId: true }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await authenticator.logout(request, { redirectTo: '/' });
  }
  const { flash, headers: flashHeaders } = await getFlashSession(request);

  const locale = await time(() => i18next.getLocale(request), {
    timings,
    type: 'get locale',
    desc: 'get locale in root'
  });

  const session = await time(() => getSession(request.headers.get('cookie')), {
    timings,
    type: 'get session',
    desc: 'get session in root'
  });

  return json(
    {
      user,
      cartId,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        session: {
          theme: await getTheme(request)
        }
      },
      appConfig: {
        googleAnalyticsId: process.env.GOOGLE_ANALYTICS_ID,
        cookieYesToken: process.env.COOKIE_YES_TOKEN,
        isProduction: process.env.NODE_ENV === 'production',
        visitorId: userId,
        sentryDsn: process.env.SENTRY_DSN
      },
      consentData: session.get('consentData'),
      ENV: getEnv(),
      flash,
      locale
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        flashHeaders,
        cartHeaders
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

function Document({
  children,
  nonce,
  locale,
  consentData,
  appConfig,
  theme = 'light',
  env = {}








}: {children: React.ReactNode;nonce: string;locale?: string;consentData?: ConsentData;appConfig?: AppConfig; // AppConfig exported from utils/env.server.ts
  theme?: 'dark' | 'light';env?: Record<string, string>;}) {let dir = useDirection();

  return (
    <html lang={locale} dir={dir} className={`${theme}`}>
			<CookieConsentProvider consentData={consentData}>
				<head>
					<ClientHintCheck nonce={nonce} />
					<ExposeAppConfig appConfig={appConfig} nonce={nonce} />
					{appConfig ?
          <GoogleAnalytics
            googleAnalyticsId={appConfig.googleAnalyticsId}
            visitorId={appConfig.visitorId}
            nonce={nonce} /> :

          null}
					<Meta />
					<meta charSet="utf-8" />
					<meta name="viewport" content="width=device-width,initial-scale=1" />
					<Links />
				</head>
				<body>
					{children}
					<ScrollRestoration nonce={nonce} />
					<Scripts nonce={nonce} />
					<script
            nonce={nonce}
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(env)}`
            }} />

					<LiveReload nonce={nonce} />
				</body>
			</CookieConsentProvider>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const user = useOptionalUser();
  // setting theme to light until dark theme is styled
  // const theme = useTheme()
  const theme = 'light';
  useToast(data.flash?.toast);

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(data.locale);

  return (
    <Document
      nonce={nonce}
      theme={theme}
      locale={data.locale}
      env={data.ENV}
      consentData={data.consentData}
      appConfig={data.appConfig}>

			<div className="flex h-screen flex-col justify-between">
				<Header user={user} />

				<Outlet
          context={{
            appConfig: data.appConfig,
            locale: data.locale
          }} />


				<Footer />
				{data.consentData ? null : <CookieConsentBanner />}
				<Confetti confetti={data.flash?.confetti} />
				<Toaster />
			</div>
			<Confetti confetti={data.flash?.confetti} />
			<Toaster />
			{RemixDevTools &&
      <Suspense>
					<RemixDevTools />
				</Suspense>}

		</Document>);

}

export default withSentry(App);

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}